// @flow
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addYears, isAfter, format as dateFmt } from 'date-fns';

import Modal from '@shared/Modal/Modal';
import Button, { buttonConstants } from '@shared/Button';

import GroupClassExportService from '@groupClass/services/GroupClassExportService';
import { ERROR_IS_BLANK } from '@helpers/TranslationHelpers';
import type { Error, ErrorProps } from '@core/types';
import { FORMAT_DATE_API } from '@app/constants/dates';
import DatePickerField from '@common/components/DateTimePicker/components/DatePickerField';
import DateService from '@common/services/DateService';

const MAX_EXPORT_RANGE_YEARS = 2;

type Props = {
  isActiveModalExport: boolean,
  toggleModalExport: () => void,
};

const ModalClassExport = (props: Props): React$Node => {
  const { isActiveModalExport, toggleModalExport } = props;
  const { t, i18n: { language } } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [ startDate, setStartDate ] = useState<Date | null>(null);
  const [ endDate, setEndDate ] = useState<Date | null>(null);
  const [ errors, setErrors ] = useState<Error[]>([]);

  useEffect(() => {
    setStartDate(null);
    setEndDate(null);
    setErrors([]);
    setIsLoading(false);
  }, [isActiveModalExport]);

  const handleChange = useCallback((date: moment$Moment, type: string): void => {
    let dateFormatted = date ? new Date(String(date)) : null;
    if (type === 'startDate') {
      setStartDate(dateFormatted);
      if (dateFormatted && endDate && isAfter(endDate, addYears(dateFormatted, MAX_EXPORT_RANGE_YEARS))) {
        setEndDate(null);
      }
    } else if (type === 'endDate') {
      setEndDate(dateFormatted);
    }
  }, [endDate, setEndDate, setStartDate]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    let errorsTmp = [];

    if (!startDate) {
      errorsTmp.push({ propertyPath: 'startDate', code: ERROR_IS_BLANK, message: '' });
    }

    if (!endDate) {
      errorsTmp.push({ propertyPath: 'endDate', code: ERROR_IS_BLANK, message: '' });
    }

    if (startDate && endDate) {
      const maxEndDate = addYears(startDate, MAX_EXPORT_RANGE_YEARS);
      if (isAfter(endDate, maxEndDate)) {
        errorsTmp.push({
          propertyPath: 'startDate',
          code: 'RANGE_TOO_LONG',
          message: t('groupClasses.errors.maxDateRange', { maxYears: MAX_EXPORT_RANGE_YEARS }),
        });
      }
    }

    setErrors(errorsTmp);

    if (startDate && endDate && errorsTmp.length === 0) {
      setIsLoading(true);

      const params = {
        startDate: dateFmt(startDate, FORMAT_DATE_API),
        endDate: dateFmt(endDate, FORMAT_DATE_API),
      };

      try {
        await GroupClassExportService.fetchExport(params);
        toggleModalExport();
      } finally {
        setIsLoading(false);
      }
    }
  }, [startDate, endDate, toggleModalExport, t]);

  const errorsProps = useMemo((): ErrorProps => {
    let errorsPropsTmp = {};
    errors.forEach((error: Error) => {
      Object.assign(errorsPropsTmp, {
        [`${ error.propertyPath }`]: {
          color: 'is-danger',
          labelProps: { className: 'has-text-danger' },
          error: {
            text: error.message,
            textColor: 'is-danger',
          },
        },
      });
    });
    return errorsPropsTmp;
  }, [errors, language]);

  const footerModalExport: React$Node = useMemo((): React$Node => (
    <footer className="modal-export-footer">
      <Button
        type={ buttonConstants.PRIMARY }
        className="button-modal"
        onClick={ handleSubmit }
        disabled={ isLoading }
      >
        { isLoading ? t('common.loading') : t('common.validate') }
      </Button>
    </footer>
  ), [handleSubmit, language, isLoading]);

  return (
    <Modal
      isActive={ isActiveModalExport }
      isClipped
      onClose={ toggleModalExport }
      title={ t('groupClasses.groupClassExport') }
      footer={ footerModalExport }
      modalContentClassName="modal-export-card"
    >
      <div className="modal-export-card">
        <p className="modal-export-text">{ t('groupClasses.modalClassExportText') }</p>
        <DatePickerField
          id="startDate"
          name="startDate"
          value={ startDate ?? null }
          label={ t('groupClasses.modalStartDate') }
          onChange={ (date) => handleChange(date, 'startDate') }
          color={ errorsProps.startDate?.color }
          labelProps={ errorsProps.startDate?.labelProps }
          helpProps={ errorsProps.startDate?.error }
          required
          minDate={ endDate ? addYears(endDate, -MAX_EXPORT_RANGE_YEARS) : null }
          maxDate={ endDate ? DateService.removeDayFromDate(endDate, 1) : null }
        />
        <div id="date-popup" />
        <DatePickerField
          id="endDate"
          name="endDate"
          value={ endDate ?? null }
          label={ t('groupClasses.modalEndDate') }
          minDate={ startDate ? DateService.addDayToDate(startDate, 1) : null }
          maxDate={ startDate ? addYears(startDate, MAX_EXPORT_RANGE_YEARS) : null }
          onChange={ (date) => handleChange(date, 'endDate') }
          isDisabled={ !startDate }
          color={ errorsProps.endDate?.color }
          labelProps={ errorsProps.endDate?.labelProps }
          helpProps={ errorsProps.endDate?.error }
          required
        />
      </div>
    </Modal>
  );
};

export default ModalClassExport;
